import {
	Box,
	Container,
	LinearProgress,
	Dialog,
	DialogContent,
	Slide,
	useMediaQuery,
	IconButton,
} from "@mui/material";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef } from "react";
import Logo from "../../public/assets/svg/logo_dark.svg";
import Authentication from "../components/Authentication";
import useAuth from "./auth";
import { useTheme } from "@mui/material/styles";
import * as gtag from "../../lib/google/gtag";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import usePageViews from "../utils/pageViews";
import * as send from "../../lib/analytics/send";

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

const BOTS = [
	"googlebot",
	"bingbot",
	"slurp",
	"duckduckbot",
	"yandexbot",
	"facebot",
	"applebot",
];

function isSafeRedirectPath(path) {
	if (!path || typeof path !== "string") return false;
	return /^\/(?!\/)/.test(path);
}

function usePremiumStatus(reloadUserInfo) {
	return useMemo(() => {
		let isBot = false;
		if (typeof window !== "undefined") {
			const ua = navigator.userAgent.toLowerCase();
			isBot = BOTS.some((bot) => ua.includes(bot));
		}

		if (isBot) return true;
		if (!reloadUserInfo?.customAttributes) return false;

		try {
			const attrs = JSON.parse(reloadUserInfo.customAttributes);
			return attrs?.userType === "PREMIUM" || attrs?.userType === "PROFESSIONAL";
		} catch {
			return false;
		}
	}, [reloadUserInfo?.customAttributes]);
}

export function withPublic(WrappedComponent) {
	const WithPublic = forwardRef((props, ref) => {
		const auth = useAuth();
		const router = useRouter();
		const { redirect_to, from } = router.query;
		const { user } = auth || {};
		const { reloadUserInfo } = user || {};
		const hasRedirectedRef = useRef(false);

		const isPremium = usePremiumStatus(reloadUserInfo);

		useEffect(() => {
			if (!auth?.user || hasRedirectedRef.current) return;
			hasRedirectedRef.current = true;

			if (redirect_to && isSafeRedirectPath(redirect_to)) {
				const query = { ...router.query };
				delete query.redirect_to;
				const queryString = new URLSearchParams(query).toString();
				router.replace(`${redirect_to}${queryString ? "?" + queryString : ""}`);
			} else if (from && isSafeRedirectPath(from)) {
				router.replace(from);
			} else {
				router.replace("/");
			}
		}, [auth?.user, redirect_to, from, router]);

		if (auth?.user) {
			return (
				<Container maxWidth="sm">
					<Box
						sx={{
							height: "70vh",
							textAlign: "center",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexDirection: "column",
						}}
					>
						<Logo />
						<Box sx={{ height: "auto", width: "50%", mx: "auto", mt: 7 }}>
							<LinearProgress />
						</Box>
					</Box>
				</Container>
			);
		}

		return <WrappedComponent ref={ref} auth={auth} {...props} gotoStripe={!isPremium} />;
	});

	WithPublic.displayName = `WithPublic(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;
	return WithPublic;
}

export function withProtected(WrappedComponent) {
	const WithProtected = forwardRef((props, ref) => {
		const auth = useAuth();
		const theme = useTheme();
		const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
		const router = useRouter();

		const { user } = auth || {};
		const { reloadUserInfo, accessToken } = user || {};

		const isPremium = usePremiumStatus(reloadUserInfo);
		const { pageViews } = usePageViews(props.pathname);

		const [open, setOpen] = useState(false);
		const [comp, setComp] = useState("");

		const handleDialogOpen = useCallback(
			(event, component) => {
				setOpen(true);
				setComp(component);
				send.uevent(
					event,
					`${auth && user ? `${component}-pricing` : `${component}-login`}-banner-open`
				);
			},
			[auth, user]
		);

		const handleDialogClose = useCallback(
			(e) => {
				setOpen(false);
				send.uevent(
					e,
					`${auth && user ? `${comp}-pricing` : `${comp}-login`}-banner-close`
				);
			},
			[auth, user, comp]
		);

		const routeHandlerDataRef = useRef({ auth, user, pageViews });
		useEffect(() => {
			routeHandlerDataRef.current = { auth, user, pageViews };
		}, [auth, user, pageViews]);

		useEffect(() => {
			const handleRouteChange = () => {
				const { auth: currentAuth, user: currentUser, pageViews: currentPageViews } = routeHandlerDataRef.current;

				if (currentAuth && !currentUser && currentPageViews > 3) {
					gtag.event({
						action: "pricing_banner",
						uid: currentUser?.uid,
					});
				}
			};

			router.events.on("routeChangeComplete", handleRouteChange);
			return () => {
				router.events.off("routeChangeComplete", handleRouteChange);
			};
		}, [router.events]);

		return (
			<>
				<WrappedComponent
					ref={ref}
					minutes={props?.minutes}
					auth={auth}
					{...props}
					openDialog={handleDialogOpen}
					isPremium={isPremium}
					pageViews={pageViews}
					token={accessToken}
				/>
				{!props?.pageLoading && (
					<Dialog
						onClose={handleDialogClose}
						open={open}
						TransitionComponent={Transition}
						keepMounted
						aria-labelledby="auth-dialog-title"
						aria-describedby="auth-dialog-description"
						maxWidth="md"
						sx={{
							"& .MuiGrid-root": auth?.user
								? {
										m: "0 !important",
										p: "0 !important",
										flexBasis: "100% !important",
										maxWidth: "100% !important",
								  }
								: {},
							"& .MuiDialog-paper": {
								maxHeight: "calc(100% - 64px)",
								width: "94% !important",
								transition: "max-height 2s ease-in-out",
								p: "0 !important",
								m: "0 !important",
								boxShadow: 0,
								textAlign: "center",
								borderRadius: "8px",
							},
							"& .MuiDialogContent-root": { p: "0 !important" },
							"& .MuiBackdrop-root": {
								backdropFilter: "blur(3px)",
							},
							borderRadius: "8px",
						}}
					>
						<IconButton
							size="small"
							aria-label="Close dialog"
							onClick={handleDialogClose}
							sx={{
								position: "absolute",
								zIndex: 2,
								right: 8,
								top: 8,
								"&:hover": { color: "#2a2d64" },
							}}
						>
							<CancelRoundedIcon fontSize="small" aria-hidden="true" />
						</IconButton>
						<DialogContent id="auth-dialog-description" sx={{ p: isMobile ? 0 : undefined }}>
							<Authentication
								maxWidth={10}
								{...props}
								isPopup={true}
								comp={comp}
								user={user}
							/>
						</DialogContent>
					</Dialog>
				)}
			</>
		);
	});

	WithProtected.displayName = `WithProtected(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;
	return WithProtected;
}
